<template>
  <div class="step-container client">
    <client-instructions></client-instructions>
    <div>
      <v-form name="CustomQuestionForm" class="form" data-test-id="custom-questions-form">
        <v-container class="client-step-container">
          <div v-if="customQuestion" data-test-id="question-container">
            <syncing-indicator 
              :page-name="MENUS.CLIENT.CUSTOM_QUESTIONS.id" 
              :instance="questionInstance"
            ></syncing-indicator>
            
            <!-- Show syncing message when syncing -->
            <div v-if="isSyncing" class="syncing-message" data-test-id="syncing-message">
              <h3 data-test-id="syncing-question">{{ customQuestion.question }}</h3>
              <p>Saving your answer...</p>
            </div>
            
            <!-- Only show the question title when form is hidden and not syncing -->
            <h2 v-else-if="formHidden" data-test-id="question-title">{{ customQuestion.question }}</h2>
            
            <!-- Show data submitted message when completed and form is hidden -->
            <data-submitted-message
              v-if="isCustomQuestionComplete && formHidden && !isSyncing"
              :submitted="isCustomQuestionComplete" 
              v-on:hide-form="toggleFormState"
              data-test-id="data-submitted-message"
            />
            
            <!-- Hide form content when syncing or when formHidden is true -->
            <div v-if="!formHidden && !isSyncing" data-test-id="form-content">
              <form-control
                ref="formControl"
                :required="customQuestion.required"
                :control="customQuestion.type"
                :question="customQuestion.question"
                :options="customQuestion.options"
                :description="customQuestion.description"
                data-test-id="form-control"
              ></form-control>
              <div class="skip-for-now">
                <a @click="skip" data-test-id="skip-button">Skip for now</a>
              </div>
            </div>
          </div>
          <div v-else data-test-id="loading-message">
            <p>Loading question...</p>
          </div>
        </v-container>
      </v-form>
    </div>
    <help-drawer v-model="showHelp" :question="customQuestion" data-test-id="help-drawer"></help-drawer>
    <div>
      <bottom-nav
        v-on:go-next="next"
        v-on:go-prev="prev"
        v-if="!navDisabled"
        data-test-id="custom-questions-nav"
      ></bottom-nav>
    </div>
  </div>
</template>

<script>
import InterviewClientMixin from '@/common/mixins/interview.client.mixin';
import BottomNav from '@/components/common/BottomNav';
import { MENUS } from '@/common/constants';
import DataSubmittedMessage from '@/components/client/DataSubmittedMessage';
import HelpDrawer from '@/components/HelpDrawerComponent';
import Themable from '@/common/mixins/themable.mixin';
import ClientInstructions from '@/components/client/dialog/InterviewInstructions';
import FormControl from "@/components/common/FormControl.vue";
import EventListener from '@/common/mixins/eventlistener.mixin';
import SyncingIndicator from '@/components/common/SyncingIndicator.vue';

export default {
  name: 'CustomQuestions',
  props: {
    questionId: String,
  },
  components: {
    BottomNav,
    ClientInstructions,
    HelpDrawer,
    DataSubmittedMessage,
    FormControl,
    SyncingIndicator
  },
  mixins: [InterviewClientMixin, Themable, EventListener],
  validations: {
    answer: {
      conditionalRequired: function(value) {
        return !this.customQuestion?.required || !!value;
      }
    }
  },
  data() {
    return {
      interviewData: null,
      customQuestion: null,
      answer: null,
      defaultPreviousRoute:
        this.lastBeneficiaryRoute || MENUS.CLIENT.BENEFICIAL_INTEREST.id,
      defaultNextRoute: MENUS.CLIENT.FINISH.id,
      navDisabled: false,
      showHelp: false,
      formHidden: false,
      MENUS: MENUS,
    };
  },
  computed: {
    answerErrors() {
      const errors = [];
      if (!this.$v.answer.$dirty) return errors;
      !this.$v.answer.required && errors.push('Answer is required');
      return errors;
    },
    isFirstCustomQuestion() {
      return this.getCustomQuestionPageIndex(this.questionId) === 0;
    },
    questionInstance() {
      return {
        name: 'question',
        label: this.questionId,
        questionId: this.questionId
      };
    },
    isCustomQuestionComplete() {
      return this.isPageComplete(
        MENUS.CLIENT.CUSTOM_QUESTIONS.id,
        null,
        this.questionId
      );
    },
    isSyncing() {
      // Check if the page is currently syncing using the SyncingIndicator logic
      const page = this.getPage(MENUS.CLIENT.CUSTOM_QUESTIONS.id, this.questionInstance);
      return page && page.status === 'IN_PROGRESS';
    },
    steps() {
      // Get all steps including complete and incomplete
      const incompleteSteps = this.incompleteSteps || [];
      
      // Include all custom questions
      const customQuestions = this.clientProgress
        ? this.clientProgress.progress.filter(
            (page) => page.pageName === MENUS.CLIENT.CUSTOM_QUESTIONS.id
          )
        : [];
      
      const customQuestionSteps = customQuestions.map(page => {
        const { _id, title } = page.data.question;
        return {
          name: title,
          route: {
            name: MENUS.CLIENT.CUSTOM_QUESTIONS.id,
            params: { questionId: _id }
          }
        };
      });
      
      // Return all steps in order
      return [...incompleteSteps, ...customQuestionSteps];
    }
  },
  mounted() {
    this.listen('update-answer', this.handleUpdateAnswer);
    
    // Check form state on mount
    this.$nextTick(() => {
      // If the question is complete, hide the form
      if (this.isCustomQuestionComplete) {
        this.formHidden = true;
      }
    });
    
    // Start polling for updates to keep synced state updated
    this.$store.dispatch('GET_INTERVIEW_PROGRESS');
  },
  created() {
    this.pages = this.interviewPages.getInterviewPages();
    this.pageIndex;
    let i = 0;
    for (const page of this.pages) {
      if (typeof page === 'object' && window.location.pathname.includes(page.pathname)) {
        this.pageIndex = i;
      }
      i++;
    }
    this.formHidden = this.isCustomQuestionComplete;
    this.fetchInterviewData();
    // Ensure we have all interview steps for navigation
    this.fetchInterviewSteps();
  },
  methods: {
    toggleFormState(hide) {
      // Update local state
      this.formHidden = hide;
      
      // Reset form validation via parent mixin method
      this.$nextTick(() => {
        if (this.$v) {
          this.$v.$reset();
        }
      });
      
      this.setNavDisabled();
    },
    /* Nav to Next Page in flow */
    next() {
      const formControlComponent = this.$refs.formControl;

      const nextRoute = this.pages[this.pageIndex + 1];

      if (formControlComponent && typeof formControlComponent.validate === 'function') {
        const isValid = formControlComponent.validate();
        if (!isValid) {
          return;
        }
      }

      if (!this.formHidden) {
        this.$v.$touch();
        if (this.$v.$invalid) return;
      } else if (!this.answer) {
        return this.goNext(nextRoute);
      }

      const data = {
        question: this.customQuestion,
        answer: this.answer,
      };

      // Mark page as syncing immediately for better user feedback
      this.markPageAsSyncing(MENUS.CLIENT.CUSTOM_QUESTIONS.id, this.questionInstance);
      
      // Add logging to help diagnose syncing indicator issues
      if (process.env.NODE_ENV !== 'production') {
        console.log('CustomQuestions: Attempting to mark as syncing', {
          pageName: MENUS.CLIENT.CUSTOM_QUESTIONS.id,
          questionId: this.questionId,
          instance: this.questionInstance
        });
        
        // Log the page after marking as syncing
        setTimeout(() => {
          const page = this.getPage(MENUS.CLIENT.CUSTOM_QUESTIONS.id, this.questionInstance);
          console.log('CustomQuestions: Page after marking as syncing', {
            found: !!page,
            status: page && page.status,
            data: page
          });
        }, 0);
      }

      this.completeStep(MENUS.CLIENT.CUSTOM_QUESTIONS.id, data).then(() => {
        // Set formHidden to true after successful completion
        this.formHidden = true;
        this.goNext(nextRoute);
      });
    },

    /* Take action from leave warning dialog */
    takeDialogAction(decision) {
      // Assuming you have a dialog component and a way to take a decision
      if (decision === 'save') {
        this.leave();
      } else if (decision === 'skip') {
        this.skip();
      }
    },

    /* Nav to Previous Page in flow */
    prev() {
      this.goPrev(this.pages[this.pageIndex - 1]);
    },

    /* Skip to next Page in flow */
    skip() {      
      // Fetch updated interview progress before navigation
      this.$store.dispatch('GET_INTERVIEW_PROGRESS').finally(() => {
        this.goNext(this.pages[this.pageIndex + 1]);
      });
    },

    fetchInterviewData() {
      this.locateCustomQuestion();
    },

    locateCustomQuestion() {
      this.customQuestion = this.getCustomQuestion(this.questionId);
    },

    toggleHelp() {
      this.showHelp = !this.showHelp;
    },
    handleUpdateAnswer(value) {
      this.answer = value
    },
    fetchInterviewSteps() {
      // Get all interview steps from the store if needed
      if (!this.steps || this.steps.length === 0) {
        this.$store.dispatch('GET_INTERVIEW_PROGRESS');
      }
    },
    // Update formHidden state when sync status changes
    handleSyncStatusChange() {
      // If completed, hide the form
      if (this.isCustomQuestionComplete) {
        this.formHidden = true;
      }
    }
  },
  watch: {
    // Watch for changes in the sync status
    isSyncing(newVal, oldVal) {
      if (oldVal === true && newVal === false) {
        // Sync has completed, check if question is complete
        this.$nextTick(() => {
          this.handleSyncStatusChange();
        });
      }
    },
    // Watch for changes in the completion status
    isCustomQuestionComplete(newVal) {
      if (newVal === true) {
        this.formHidden = true;
      }
    }
  },
};
</script>
<style lang="scss" scoped>
::v-deep .my-textarea textarea {
  padding: 10px !important;
}

.syncing-message {
  text-align: center;
  margin: 20px 0;
  
  h3 {
    margin-bottom: 10px;
  }
  
  p {
    color: #666;
    font-style: italic;
  }
}
</style>
